/*!
 * Icelantic Ski Finder — engine + UI (dependency-free)
 * v3.2.2 · 26/27 lineup · 2026-09-09 · Forge Digital Marketing
 *   v3.2.2: SCROLL FIX. Advancing a question used to call root.scrollIntoView({block:"start"}), which
 *         pinned the top of the quiz to the top of the viewport — i.e. underneath the theme's sticky
 *         header, hiding the progress bar and the question title. And because the page template stacks
 *         a large hero above the quiz, the old `r.top > 120` test was true on nearly every step, so it
 *         scrolled the page DOWN on every click. Now: measure any fixed/sticky header, scroll to the
 *         quiz top minus that offset, and don't scroll at all when the question is already readable.
 *         Honours prefers-reduced-motion. Engine untouched — persona matrix still valid.
 *   v3.2: email capture sends the FULL result as a "Ski Finder Email Captured" event via Klaviyo's
 *         client events API (raw fetch — survives ad blockers, no half-populated sends). The results
 *         flow triggers on that metric and reads {{ event.sf_* }}. List subscribe = marketing consent
 *         only. listId is a section setting (klaviyo_list_id) so it never needs a code deploy again.
 *   v3.1: live Klaviyo list subscribe wired.
 *
 * v3 REMAP (per Ben's sheet + the 8/26 & 8/28 calls + his 8/31 emails):
 *   · Six graded conditions per ski (Ben + John): flex, groomer, powder, bumps, park, backcountry.
 *   · Groomers scored from the groomer rating (waist proxy deleted). Backcountry is a sixth terrain option.
 *   · Style (Q5) scored against FLEX — "Fast & powerful" now steers instead of being neutral.
 *   · Q1 = Men's / Women's / No preference. Q7 = height only (Ben's size charts; weight/build removed).
 *   · Sizing = chart lookup → snap to the ski's real made lengths; out-of-range → NP/Scout fallback card.
 *   · Days weight 0 (data-mining only). No numeric score shown to shoppers.
 *
 * HOW IT LOADS
 *   The Liquid section renders two JSON blobs into the page:
 *     <script type="application/json" id="ski-finder-data">   { "skis": [...] }
 *     <script type="application/json" id="ski-finder-config"> { "weights": {...}, ... }
 *   and a mount point:  <div id="ski-finder-app" …>. Then this file runs SkiFinder.init().
 *   Everything is client-side; no backend, no per-use cost.
 *
 * THE ENGINE IS THE VALIDATED 2 SEP 2026 BUILD. If you change scoreSki/recommend/recommendLength,
 * re-run tests/persona-matrix-2627.csv (expected #1, scores, lengths) — all 14 rows must still pass.
 *
 * SIZE CHARTS: sizeCharts below is Ben's SIZE CHARTS tab, verified cell-by-cell 9 Sep 2026 (38/38 rows).
 * The public page /pages/ski-size-chart was rebuilt from the same source the same day — keep them in step.
 */
(function () {
  "use strict";

  /* ============ 1. DATA + CONFIG ============ */
  const readJSON = (id, fallback) => { try { const el = document.getElementById(id); return el ? JSON.parse(el.textContent) : fallback; } catch (e) { console.error("[SkiFinder] bad JSON in #" + id, e); return fallback; } };

  const DEFAULT_CONFIG = {
    weights: { terrain: 65, waist: 20, character: 15, versatility: 0 },   // days de-weighted per Ben (8/26 call)
    regionIdealWaist: { rockies: 102, pnw: 105, east: 90, midwest: 88, other: 96 },
    regionAdjust: { powderPrimary: 8, groomersPrimary: -6, everythingPrimary: -4, powderSecondary: 3, groomersSecondary: -3 },
    waistTolerance: 20,
    charIdeal: { playful: 5, smooth: 7, chargy: 9 },                      // Q5 vs FLEX (personality_scale)
    charFalloff: 5,
    tieBreak: ["nomad-100", "nomad-106", "maiden-102", "torrent-96", "tempest-94"], // live handles (year-less since Ben's 8/26 upload)
    labels: { perfect: 80, great: 65 },          // badge thresholds — the number itself is never shown
    minPool: 3,                                  // hero always comes from the strict ability pool; ±1 "stretch" skis may fill ALTERNATE slots only
    sizeChartUrl: "/pages/ski-size-chart",
    klaviyo: { companyId: "JYs2PE", listId: "XxmVWK" },   // Icelantic account · "Ski Finder Leads" list XxmVWK (single opt-in, recreated 9 Sep). Overridden by section setting klaviyo_list_id.
    /* Ben's size charts (SIZE CHARTS tab, adult tables) — real made lengths per line. */
    sizeCharts: {
      unisex: { heights: [147,150,152,155,157,160,163,165,168,170,173,175,178,180,183,185,188,190,193],
        rows: [[133,143,153,155],[143,143,153,155],[143,155,162,166],[153,155,166,166],[153,155,166,171],
               [153,155,166,171],[155,155,166,171],[155,162,166,171],[166,171,171,176],[166,171,176,176],
               [166,171,176,182],[171,171,176,182],[171,171,176,182],[171,176,182,182],[176,176,182,182],
               [176,182,188,188],[182,182,188,188],[182,182,188,194],[182,182,188,194]] },
      womens: { heights: [147,150,152,155,157,160,163,165,168,170,173,175,178,180,183,185,188,190,193],
        rows: [[133,143,150,150],[143,143,150,150],[143,150,155,162],[150,150,155,162],[150,150,155,162],
               [150,155,162,169],[155,155,162,169],[155,162,169,169],[155,162,169,169],[162,162,169,176],
               [162,162,169,176],[162,169,176,176],[169,169,176,176],[169,169,176,182],[169,176,182,182],
               [176,176,182,188],[176,182,188,188],[182,182,188,188],[182,182,188,188]] }
    },
    /* Ben 8/31: chart lengths outside a ski's made range exist only in Scout youth + NP (unisex) skis;
       his preferred single filler is the Rocky Mountain NP 100 ("our top seller"), Scout for the smallest.
       NOTE 9 Sep: the youth URL is /collections/scout-youth-collection — /collections/scout-youth 404s.
       The Liquid section also emits sizeFallback into #ski-finder-config, which overrides this block. */
    sizeFallback: {
      short: { name: "Rocky Mountain NP 100", url: "/products/rocky-mtn-np-nomad-100", min: 155, max: 188 },
      youth: { name: "Scout youth skis", url: "/collections/scout-youth-collection", min: 133, max: 153 }
    },
    /* Ben's end-of-call direction (8/26): Saba/Nia/Shaman ride along as secondary suggestions. */
    alsoLike: {
      "maiden-108": { name: "Nia Pro 105", url: "/products/25-26-nia-pro-105" },
      "nomad-112": { name: "Saba Pro 117", url: "/products/25-26-saba-pro-117" },
      "torrent-102": { name: "Shaman 110", url: "/products/25-26-shaman-110" }
    }
  };

  const data = readJSON("ski-finder-data", { skis: [] });
  const CONFIG = Object.assign({}, DEFAULT_CONFIG, readJSON("ski-finder-config", {}));
  const SKIS = (data.skis || []).map(s => Object.assign({ flex: 7, groomer: 0, touring: 0, lengths: [], img: "", isNew: false, why: "" }, s));

  /* ============ 2. ENGINE (validated: 14-persona matrix + 25,920-path enumeration, 2 Sep) ============ */
  const clamp = (v, l, h) => Math.max(l, Math.min(h, v));
  const stdev = a => { const m = a.reduce((s, x) => s + x, 0) / a.length; return Math.sqrt(a.reduce((s, x) => s + (x - m) ** 2, 0) / a.length); };

  /* Every terrain option reads a Ben-graded rating directly.
     "everything" rewards an even spread across the resort trio (park excluded by design). */
  function terrainScore(s, k) {
    switch (k) {
      case "powder": return s.powder / 10;
      case "bumps_trees": return s.bumps / 10;
      case "park": return s.park / 10;                 // label: Park & Street
      case "backcountry": return (s.touring ?? 0) / 10; // Ben's BACKCOUNTRY column
      case "groomers": return (s.groomer ?? 0) / 10;    // Ben's GROOMER/HARDPACK column
      case "everything": return clamp(1 - stdev([s.groomer ?? 0, s.powder, s.bumps]) / 5, 0, 1);
      default: return 0;
    }
  }
  /* Style vs FLEX (0 = soft/playful → 10 = stiff/aggressive). chargy→9 steers to the stiff chargers. */
  function charScore(style, flex) { const i = CONFIG.charIdeal[style] ?? 7; return clamp(1 - Math.abs(flex - i) / CONFIG.charFalloff, 0, 1); }

  function scoreSki(s, a) {
    const W = CONFIG.weights;
    const terrain = ((terrainScore(s, a.terrain1) * 2 + terrainScore(s, a.terrain2)) / 3) * W.terrain;
    let ideal = CONFIG.regionIdealWaist[a.region] ?? 96; const j = CONFIG.regionAdjust;
    if (a.terrain1 === "powder") ideal += j.powderPrimary; if (a.terrain1 === "groomers") ideal += j.groomersPrimary;
    if (a.terrain1 === "everything") ideal += j.everythingPrimary;
    if (a.terrain2 === "powder") ideal += j.powderSecondary; if (a.terrain2 === "groomers") ideal += j.groomersSecondary;
    const waist = Math.max(0, 1 - Math.abs(s.waist - ideal) / CONFIG.waistTolerance) * W.waist;
    const character = charScore(a.style, s.flex) * W.character;
    const lowDays = a.days === "1-5" || a.days === "6-15";
    const versatility = (lowDays ? (stdev([s.powder, s.bumps, s.park]) < 2 ? 1 : 0.4) : 0.8) * W.versatility; // dormant at weight 0
    return { terrain, waist, character, versatility, ideal, total: terrain + waist + character + versatility };
  }

  /* Q1: mens → unisex line · womens → women's-specific · all → everything. */
  function genderKeep(s, g) { return g === "all" || (g === "womens" ? s.gender === "womens" : s.gender === "unisex"); }

  /* The HERO always comes from the strict ability pool (Ben's ability_level data is law).
     If that pool is short of minPool, ±1-ability "stretch" skis fill ALTERNATE slots only (stretch:true). */
  function recommend(a) {
    const pri = h => { const i = CONFIG.tieBreak.indexOf(h); return i === -1 ? Infinity : i; };
    const rank = p => p.map(s => Object.assign({ ski: s }, scoreSki(s, a)))
      .sort((x, y) => y.total - x.total || pri(x.ski.handle) - pri(y.ski.handle));
    const strict = SKIS.filter(s => genderKeep(s, a.gender) && a.ability >= s.abMin && a.ability <= s.abMax);
    let out = rank(strict);
    if (out.length < CONFIG.minPool) {
      const extra = SKIS.filter(s => genderKeep(s, a.gender) && strict.indexOf(s) === -1 && a.ability >= s.abMin - 1 && a.ability <= s.abMax + 1);
      out = out.concat(rank(extra).map(r => Object.assign({}, r, { stretch: true })));
    }
    return out.slice(0, 3);
  }

  /* Length = Ben's chart (chart chosen by the SKI's line), snapped to what the ski comes in.
     Out-of-range → fallback card. */
  function chartLength(chartKey, heightCm, ability) {
    const c = CONFIG.sizeCharts[chartKey] || CONFIG.sizeCharts.unisex;
    const h = clamp(heightCm, c.heights[0], c.heights[c.heights.length - 1]);
    let ri = 0; for (let i = 0; i < c.heights.length; i++) if (Math.abs(c.heights[i] - h) < Math.abs(c.heights[ri] - h)) ri = i;
    return c.rows[ri][clamp(ability, 1, 4) - 1];
  }
  function recommendLength(ski, a) {
    if (!a.heightCm || !ski.lengths || !ski.lengths.length) return null;
    const target = chartLength(ski.gender === "womens" ? "womens" : "unisex", a.heightCm, a.ability);
    let best = ski.lengths[0];
    for (const L of ski.lengths) { if (Math.abs(L - target) < Math.abs(best - target)) best = L; else if (Math.abs(L - target) === Math.abs(best - target)) best = a.ability >= 3 ? Math.max(best, L) : Math.min(best, L); }
    const outOfRange = target < ski.lengths[0] || target > ski.lengths[ski.lengths.length - 1];
    return { length: best, target: target, gap: target - best, outOfRange: outOfRange };
  }
  const strength = t => t >= CONFIG.labels.perfect ? "Perfect match" : t >= CONFIG.labels.great ? "Great match" : "Good match";

  /* ============ 3. ANALYTICS (Klaviyo) ============ */
  function track(ev, props) {
    try {
      if (window.klaviyo && typeof window.klaviyo.push === "function") window.klaviyo.push(["track", ev, props]);
      else if (Array.isArray(window._learnq)) window._learnq.push(["track", ev, props]);
    } catch (e) { /* never break the quiz for analytics */ }
    if (CONFIG.debug) console.log("[SkiFinder]", ev, props);
  }
  function identify(email, props) {
    try {
      const payload = Object.assign({ $email: email }, props);
      if (window.klaviyo && typeof window.klaviyo.push === "function") window.klaviyo.push(["identify", payload]);
      else if (Array.isArray(window._learnq)) window._learnq.push(["identify", payload]);
    } catch (e) { /* noop */ }
  }
  /* Subscribes the email to the "Ski Finder Leads" list via Klaviyo's client API.
     Fire-and-forget: never blocks or breaks the quiz; silently no-ops if unconfigured. */
  function subscribeToList(email) {
    try {
      const k = CONFIG.klaviyo || {};
      if (!k.companyId || !k.listId || typeof fetch !== "function") return;
      fetch("https://a.klaviyo.com/client/subscriptions/?company_id=" + encodeURIComponent(k.companyId), {
        method: "POST",
        headers: { "Content-Type": "application/json", revision: "2024-10-15" },
        keepalive: true,
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: { custom_source: "Ski Finder", profile: { data: { type: "profile", attributes: { email: email } } } },
            relationships: { list: { data: { type: "list", id: k.listId } } }
          }
        })
      }).catch(function () { /* noop */ });
    } catch (e) { /* never break the quiz for analytics */ }
  }

  /* v3.2 — the event the results flow triggers on. Posted straight to Klaviyo's client events API so it
     lands even when klaviyo.js is blocked; props = the full result, frozen at send time. Fire-and-forget. */
  function sendClientEvent(email, metric, props) {
    try {
      const k = CONFIG.klaviyo || {};
      if (!k.companyId || typeof fetch !== "function") return;
      const clean = {}; Object.keys(props).forEach(key => { if (props[key] !== undefined && props[key] !== null) clean[key] = props[key]; });
      fetch("https://a.klaviyo.com/client/events/?company_id=" + encodeURIComponent(k.companyId), {
        method: "POST",
        headers: { "Content-Type": "application/json", revision: "2024-10-15" },
        keepalive: true,
        body: JSON.stringify({
          data: {
            type: "event",
            attributes: {
              metric: { data: { type: "metric", attributes: { name: metric } } },
              properties: clean,
              profile: { data: { type: "profile", attributes: { email: email, properties: clean } } }
            }
          }
        })
      }).catch(function () { /* noop */ });
    } catch (e) { /* never break the quiz for analytics */ }
    if (CONFIG.debug) console.log("[SkiFinder] client event", metric, props);
  }

  /* ============ 4. COPY ============ */
  const LBL = {
    ability: { 1: "beginner", 2: "intermediate", 3: "advanced", 4: "expert" },
    region: { rockies: "in the Rockies", pnw: "in the Pacific Northwest", east: "on the East Coast", midwest: "in the Midwest", other: "wherever the snow falls" },
    terrain: { groomers: "groomers", powder: "powder", bumps_trees: "bumps & trees", park: "park & street laps", backcountry: "backcountry lines", everything: "a bit of everything" },
    terrainTitle: { groomers: "Groomers", powder: "Powder", bumps_trees: "Bumps & Trees", park: "Park & Street", backcountry: "Backcountry", everything: "A Bit of Everything" },
    styleTitle: { smooth: "Smooth & controlled", playful: "Playful & poppy", chargy: "Fast & powerful" },
    regionTitle: { rockies: "Rockies & West", pnw: "Pacific Northwest", east: "East Coast", midwest: "Midwest", other: "Europe & elsewhere" },
    benefit: { powder: "float, quickness, and a ski that loves the deep stuff", groomers: "edge hold, energy, and clean acceleration out of every turn", bumps_trees: "a quick, intuitive ski that threads tight lines", park: "pop, balance, and a ski that's built to take a beating", backcountry: "a stable, floaty ski that's happy past the ropes", everything: "one ski that says yes to the whole mountain" },
    bars: [["GROOMERS", "groomer", "groomers"], ["POWDER", "powder", "powder"], ["BUMPS & TREES", "bumps", "bumps_trees"], ["PARK & STREET", "park", "park"], ["BACKCOUNTRY", "touring", "backcountry"]]
  };
  const QUESTIONS = [
    { key: "gender", num: "01", kicker: "Who", title: "First things first — who's getting on these skis?", hint: "Every Icelantic is handbuilt for everyone; women's models are tuned for different weight distribution and boot sizes.", opts: [{ v: "mens", t: "Men's skis" }, { v: "womens", t: "Women's skis" }, { v: "all", t: "No preference — show me everything" }] },
    { key: "ability", num: "02", kicker: "Ability", title: "How would you describe your skiing?", opts: [{ v: 1, t: "Beginner", d: "Greens and easy blues, still building confidence" }, { v: 2, t: "Intermediate", d: "Comfortable on blues, poking at blacks" }, { v: 3, t: "Advanced", d: "Blacks, bumps, and trees are home turf" }, { v: 4, t: "Expert", d: "If it's on the mountain, I ski it" }] },
    { key: "region", num: "03", kicker: "Home mountain", title: "Where do you ski most?", hint: "Snow is different everywhere — this helps us dial your width.", opts: [{ v: "rockies", t: "Rockies & West" }, { v: "pnw", t: "Pacific Northwest" }, { v: "east", t: "East Coast" }, { v: "midwest", t: "Midwest" }, { v: "other", t: "Europe & Somewhere else" }] },
    { key: "terrain", num: "04", kicker: "Terrain", title: "What terrain calls your name?", hint: "Pick one or two.", multi: 2, grid: true, opts: [{ v: "groomers", t: "Corduroy & groomers" }, { v: "powder", t: "Powder, please" }, { v: "bumps_trees", t: "Bumps & trees" }, { v: "park", t: "Park & street" }, { v: "backcountry", t: "Backcountry" }, { v: "everything", t: "A bit of everything" }] },
    { key: "style", num: "05", kicker: "Style", title: "What's your style on snow?", opts: [{ v: "smooth", t: "Smooth & controlled", d: "Clean turns, planted feel" }, { v: "playful", t: "Playful & poppy", d: "Side hits, butters, surfing the hill" }, { v: "chargy", t: "Fast & powerful", d: "Charge hard, trench turns" }] },
    { key: "days", num: "06", kicker: "Days", title: "How many days a year do you ski?", opts: [{ v: "1-5", t: "1–5", d: "Make 'em count" }, { v: "6-15", t: "6–15", d: "Solid season" }, { v: "16-30", t: "16–30", d: "Committed" }, { v: "30+", t: "30+", d: "Basically live there" }] },
    { key: "size", num: "07", kicker: "Size", title: "Let's nail your size.", hint: "Your height and ability pick your length — same size charts our team uses at demos.", custom: "size" }
  ];

  /* ============ 5. UI ============ */
  let root, screens, state = { answers: {}, step: 0 };
  const esc = s => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
  const cmToFtIn = cm => { const i = Math.round(cm / 2.54); return `${Math.floor(i / 12)}'${i % 12}"`; };
  const save = () => { try { sessionStorage.setItem("skiFinder", JSON.stringify(state)); } catch (e) { } };
  const $ = (sel, el) => (el || root).querySelector(sel);

  function setProgress() {
    const total = QUESTIONS.length, fill = $("[data-sf-progress-fill]"), label = $("[data-sf-step]"), bar = $("[data-sf-progress]");
    const pct = state.step >= total ? 100 : ((state.step + 1) / total) * 100 * (state.step === 0 ? 0.07 : 1);
    if (fill) fill.style.width = pct + "%";
    if (bar) bar.setAttribute("aria-valuenow", Math.min(state.step + 1, total));
    if (label) label.textContent = state.step >= total ? "YOUR RESULTS" : `Q ${String(state.step + 1).padStart(2, "0")} / ${String(total).padStart(2, "0")}`;
  }
  function render() { setProgress(); save(); if (state.step >= QUESTIONS.length) return renderResults(); renderQuestion(QUESTIONS[state.step]); }

  /* v3.2.2 — how much of the top of the viewport is covered by a fixed/sticky theme header right now.
     Measured live rather than hard-coded, so a header that shrinks on scroll doesn't throw the maths off. */
  function stickyOffset() {
    let off = 0;
    try {
      const els = document.querySelectorAll("header, .header, .site-header, .header-wrapper, .shopify-section-header, [data-header], [data-sticky-header]");
      for (let i = 0; i < els.length; i++) {
        const el = els[i], cs = window.getComputedStyle(el);
        if (cs.position !== "fixed" && cs.position !== "sticky") continue;
        if (cs.visibility === "hidden" || cs.display === "none") continue;
        const r = el.getBoundingClientRect();
        if (r.height > 0 && r.top <= 4 && r.bottom > 0) off = Math.max(off, r.bottom);
      }
    } catch (e) { /* if anything about the theme surprises us, fall back to no offset */ }
    return Math.min(off, 160);
  }

  /* v3.2.2 — bring the new question into view without burying it under the sticky header, and without
     yanking the page when it is already perfectly readable. Old behaviour scrolled DOWN on every click
     (the page's hero pushes the quiz below the fold) and aligned the quiz top to y=0, behind the header. */
  function scrollToApp() {
    if (!root) return;
    const r = root.getBoundingClientRect();
    const pad = stickyOffset() + 16;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (r.top >= pad - 8 && r.top <= vh * 0.5) return;   // already readable — leave the page where it is
    const y = Math.max(0, (window.pageYOffset || document.documentElement.scrollTop || 0) + r.top - pad);
    const reduce = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    try { window.scrollTo({ top: y, behavior: reduce ? "auto" : "smooth" }); }
    catch (e) { window.scrollTo(0, y); }
  }

  function renderQuestion(q) {
    const a = state.answers; let body = "";
    if (q.custom === "size") {
      const h = a.heightCm || 173;
      body = `<div class="sf-readout" data-sf-hv>${cmToFtIn(h)}<small>${h} cm</small></div>
       <input type="range" min="140" max="205" value="${h}" data-sf-range aria-label="Your height">
       <button class="sf-skip" type="button" data-sf-skip>Skip — show my ski without sizing</button>`;
    } else {
      const sel = v => q.multi ? (a[q.key] || []).includes(v) : a[q.key] === v;
      const idx = v => (a[q.key] || []).indexOf(v) + 1;
      body = `<div class="sf-opts ${q.grid ? "sf-grid2" : ""}" role="group" aria-label="${esc(q.title)}">${q.opts.map(o => `
       <button class="sf-opt" type="button" data-v="${o.v}" aria-pressed="${sel(o.v)}">${q.multi ? `<span class="n">${idx(o.v) || ""}</span>` : ""}<span class="t">${esc(o.t)}</span>${o.d ? `<span class="d">${esc(o.d)}</span>` : ""}</button>`).join("")}</div>`;
    }
    screens.innerHTML = `<div class="sf-screen">
      <span class="sf-eyebrow">Question ${q.num} — ${q.kicker}</span>
      <h2 class="sf-h2">${esc(q.title)}</h2>
      ${q.hint ? `<p class="sf-hint">${esc(q.hint)}</p>` : ""}
      ${body}
     </div>
     <div class="sf-nav">
      <button class="sf-btn-ghost" type="button" data-sf-back aria-label="Back" ${state.step === 0 ? "disabled" : ""}>←</button>
      <button class="sf-btn sf-btn--block" type="button" data-sf-next disabled>${state.step === QUESTIONS.length - 1 ? "See my ski" : "Continue"}</button>
     </div>`;
    const next = $("[data-sf-next]"), back = $("[data-sf-back]");
    const ready = () => q.custom === "size" ? true : q.multi ? (a[q.key] || []).length >= 1 : a[q.key] !== undefined;
    const sync = () => { next.disabled = !ready(); };
    if (q.custom === "size") {
      a.heightCm = a.heightCm || 173;
      $("[data-sf-range]").oninput = e => { a.heightCm = +e.target.value; $("[data-sf-hv]").innerHTML = `${cmToFtIn(a.heightCm)}<small>${a.heightCm} cm</small>`; save(); };
      $("[data-sf-skip]").onclick = () => { a.heightCm = null; state.step++; render(); scrollToApp(); };
    } else {
      root.querySelectorAll(".sf-opt[data-v]").forEach(b => b.onclick = () => {
        const v = isNaN(+b.dataset.v) ? b.dataset.v : +b.dataset.v;
        if (q.multi) {
          let arr = a[q.key] || []; if (arr.includes(v)) arr = arr.filter(x => x !== v); else { arr.push(v); if (arr.length > q.multi) arr.shift(); } a[q.key] = arr;
          root.querySelectorAll(".sf-opt[data-v]").forEach(x => { const xv = isNaN(+x.dataset.v) ? x.dataset.v : +x.dataset.v; const i = arr.indexOf(xv); x.setAttribute("aria-pressed", i > -1); x.querySelector(".n").textContent = i > -1 ? i + 1 : ""; });
        } else { a[q.key] = v; root.querySelectorAll(".sf-opt[data-v]").forEach(x => x.setAttribute("aria-pressed", x === b)); if (state.step === 0) track("Ski Finder Started", {}); }
        sync(); save();
      });
    }
    next.onclick = () => { if (ready()) { state.step++; render(); scrollToApp(); } };
    back.onclick = () => { if (state.step > 0) { state.step--; render(); scrollToApp(); } };
    sync();
  }

  const skiImg = s => s.img ? `<img src="${esc(s.img)}" alt="${esc(s.name)} topsheet" loading="lazy" onerror="this.replaceWith(Object.assign(document.createElement('span'),{className:'ph',textContent:'${esc(s.name)} · topsheet'}))">` : `<span class="ph">${esc(s.name)} · topsheet</span>`;

  function answers() {
    const a = state.answers;
    return { gender: a.gender, ability: a.ability, region: a.region, terrain1: (a.terrain || [])[0] || "everything", terrain2: (a.terrain || [])[1] || (a.terrain || [])[0] || "everything", style: a.style, days: a.days, heightCm: a.heightCm };
  }
  function profileProps(ans, win, len, alts) {
    const fb = sizeFallbackFor(len);
    return {
      sf_gender_line: ans.gender, sf_ability: LBL.ability[ans.ability], sf_region: ans.region, sf_region_label: LBL.regionTitle[ans.region],
      sf_terrain_1: ans.terrain1, sf_terrain_2: ans.terrain2, sf_terrain_1_label: LBL.terrainTitle[ans.terrain1], sf_terrain_2_label: LBL.terrainTitle[ans.terrain2],
      sf_style: ans.style, sf_style_label: LBL.styleTitle[ans.style], sf_days_per_season: ans.days, sf_height_cm: ans.heightCm || undefined,
      sf_recommended_ski: win.ski.handle, sf_recommended_ski_title: win.ski.name, sf_recommended_img: win.ski.img || undefined, sf_recommended_url: win.ski.url,
      sf_recommended_is_new: !!win.ski.isNew, sf_ski_length_min: win.ski.lengths[0], sf_ski_length_max: win.ski.lengths[win.ski.lengths.length - 1],
      sf_recommended_length: len ? len.length : undefined, sf_size_target: len ? len.target : undefined, sf_size_out_of_range: len ? !!len.outOfRange : undefined,
      sf_size_fallback_name: fb ? fb.name : undefined, sf_size_fallback_url: fb ? fb.url : undefined,
      sf_match_score: Math.round(win.total), sf_match_label: strength(win.total), sf_alternates: alts.map(t => t.ski.handle), sf_alternate_titles: alts.map(t => t.ski.name),
      sf_completed_at: new Date().toISOString()
    };
  }
  /* Same fallback the size card shows when the chart target is outside the ski's made range (Ben 8/31). */
  function sizeFallbackFor(len) {
    if (!len || !len.outOfRange) return null;
    const fb = CONFIG.sizeFallback || {}; const shorter = len.gap < 0;
    if (shorter && fb.youth && len.target < (fb.short ? fb.short.min : 155)) return fb.youth;
    return fb.short || null;
  }

  /* Size card: normal → snapped length; out-of-range → honest note + Ben's fallback (RMNP 100 / Scout). */
  function sizeCardHTML(ans, win, len) {
    if (!len) return `<span class="k">Sizing</span><p style="margin-top:10px">You skipped sizing — check the <a href="${esc(CONFIG.sizeChartUrl)}">size chart</a>, or retake with your height for a dialed length.</p>`;
    const abil = LBL.ability[ans.ability].toUpperCase();
    if (!len.outOfRange) return `<span class="k">Your size</span><div class="v">${len.length}cm<small>${cmToFtIn(ans.heightCm)} · ${abil}</small></div><p>Between sizes? Size down for quicker turns, up for more stability and float.</p>`;
    const fb = CONFIG.sizeFallback || {}; const shorter = len.gap < 0;
    const alt = shorter && fb.youth && len.target < (fb.short ? fb.short.min : 155)
      ? `Our <a href="${esc(fb.youth.url)}">${esc(fb.youth.name)}</a> (${fb.youth.min}–${fb.youth.max}cm) fit smaller skiers perfectly.`
      : fb.short ? `The <a href="${esc(fb.short.url)}">${esc(fb.short.name)}</a> — same shape family, made ${fb.short.min}–${fb.short.max}cm — covers your size.` : "";
    return `<span class="k">Your size</span><div class="v">${len.target}cm<small>${cmToFtIn(ans.heightCm)} · ${abil}</small></div>
      <p>Our chart puts you at ${len.target}cm — ${shorter ? "shorter" : "longer"} than the ${esc(win.ski.name)} is made (${win.ski.lengths[0]}–${win.ski.lengths[win.ski.lengths.length - 1]}cm). Closest fit: <b>${len.length}cm</b>. ${alt}</p>`;
  }

  function renderResults() {
    const ans = answers();
    const top3 = recommend(ans);
    if (!top3.length) { screens.innerHTML = `<div class="sf-screen"><p>Hmm — no match. <button class="sf-skip" type="button" data-sf-reset>Start over</button></p></div>`; $("[data-sf-reset]").onclick = reset; return; }
    const win = top3[0], len = recommendLength(win.ski, ans), alts = top3.slice(1);
    track("Ski Finder Completed", Object.assign({ recommended: win.ski.handle, length: len ? len.length : null, alternates: alts.map(t => t.ski.handle) }, ans));
    const an = ans.ability === 1 ? "a" : "an";
    const t1 = LBL.terrain[ans.terrain1], t2 = LBL.terrain[ans.terrain2]; const terr = ans.terrain1 === ans.terrain2 ? t1 : `${t1} and ${t2}`;
    const bars = LBL.bars.map(([label, k, tk]) => { const v = win.ski[k] ?? 0; const hot = tk === ans.terrain1 || tk === ans.terrain2; return `<div class="sf-bar ${hot ? "hot" : ""}"><div class="row"><span>${label}</span><span>${v}/10</span></div><div class="track"><i data-w="${v * 10}"></i></div></div>`; }).join("");
    const picked = [...new Set([ans.terrain1, ans.terrain2])].map(t => { const b = LBL.bars.find(x => x[2] === t); return b ? `You picked ${LBL.terrain[t]} — the ${win.ski.name} scores ${win.ski[b[1]] ?? 0}/10 there.` : null; }).filter(Boolean).join(" ");
    const stretchAlts = alts.filter(t => t.stretch), trueAlts = alts.filter(t => !t.stretch);
    const altCard = t => `<a class="sf-altcard" href="${esc(t.ski.url)}"><div class="img">${skiImg(t.ski)}</div><div><div class="nm">${esc(t.ski.name)}</div><div class="why">${esc(t.ski.why)}</div></div></a>`;
    const also = CONFIG.alsoLike && CONFIG.alsoLike[win.ski.handle];
    screens.innerHTML = `<div class="sf-res">
      <div class="sf-reveal"><span class="sf-eyebrow">We found your ski</span><p>You're ${an} <b>${LBL.ability[ans.ability]}</b> skier chasing <b>${terr}</b> <b>${LBL.region[ans.region]}</b> — you need ${LBL.benefit[ans.terrain1]}. Meet the <span class="sn">${esc(win.ski.name)}</span>.</p></div>
      <div class="sf-res-grid">
       <div class="sf-card sf-hero-card">
        <div class="sf-topsheet">${skiImg(win.ski)}</div>
        <span class="sf-badge">${strength(win.total)}</span>
        <h2 class="sf-h2">${esc(win.ski.name)}</h2>
        <p class="why">${esc(win.ski.why)}</p>
        <div class="sf-bars">${bars}</div>
        ${picked ? `<div class="sf-picked">${picked}</div>` : ""}
       </div>
       <div class="sf-stack">
        <div class="sf-card sf-size-card">${sizeCardHTML(ans, win, len)}</div>
        <a class="sf-btn sf-btn--block" href="${esc(win.ski.url)}">${win.ski.isNew ? `${esc(win.ski.name)} — new for 26/27 →` : `See the ${esc(win.ski.name)} →`}</a>
        ${also ? `<div class="sf-also">Feeling adventurous? You might also like the <a href="${esc(also.url)}">${esc(also.name)}</a> →</div>` : ""}
        ${trueAlts.length ? `<div><div class="sf-alt-h">Also great for you</div><div class="sf-alts">${trueAlts.map(altCard).join("")}</div></div>` : ""}
        ${stretchAlts.length ? `<div><div class="sf-alt-h">When you level up</div><div class="sf-alts">${stretchAlts.map(altCard).join("")}</div></div>` : ""}
        <div class="sf-card sf-capture"><h3>Save your ski match</h3><p>Not ready to decide today? We'll email your match, your size, and why it works for you — so it's all there when you are.</p><input type="email" data-sf-email placeholder="you@email.com" autocomplete="email" aria-label="Email address"><button class="sf-btn sf-btn--block" type="button" data-sf-send>Email My Results</button><div class="done" data-sf-done>✓ Sent. Check your inbox — and your snow dance schedule.</div><div class="fine">No spam, just skis. Unsubscribe anytime.</div></div>
        <button class="sf-retake" type="button" data-sf-retake>Not feeling it? Retake the quiz</button>
       </div>
      </div>
     </div>`;
    requestAnimationFrame(() => requestAnimationFrame(() => root.querySelectorAll(".sf-bar .track i").forEach(b => b.style.width = b.dataset.w + "%")));
    $("[data-sf-send]").onclick = () => {
      const input = $("[data-sf-email]"), e = input.value.trim();
      if (!/^\S+@\S+\.\S+$/.test(e)) { input.style.borderColor = "var(--sf-rust)"; input.focus(); return; }
      const props = profileProps(ans, win, len, alts);
      identify(e, props);                                   // profile properties — for segments/campaigns
      sendClientEvent(e, "Ski Finder Email Captured", props); // THE flow trigger — full result frozen on the event (v3.2)
      subscribeToList(e);                                   // "Ski Finder Leads" — marketing consent only
      input.style.display = "none"; $("[data-sf-send]").style.display = "none"; $("[data-sf-done]").style.display = "block";
    };
    $("[data-sf-retake]").onclick = reset;
  }
  function reset() { state = { answers: {}, step: 0 }; try { sessionStorage.removeItem("skiFinder"); } catch (e) { } render(); scrollToApp(); }

  function init(mount) {
    root = typeof mount === "string" ? document.querySelector(mount) : (mount || document.getElementById("ski-finder-app"));
    if (!root) return;
    screens = root.querySelector("[data-sf-screens]") || root;
    if (!SKIS.length) { screens.innerHTML = "<p>The Ski Finder is warming up — no eligible skis were found. Check custom.finder_eligible on the products.</p>"; return; }
    try { const saved = sessionStorage.getItem("skiFinder"); if (saved) state = JSON.parse(saved); } catch (e) { }
    render();
  }

  window.SkiFinder = { init, recommend, recommendLength, chartLength, scoreSki, scrollToApp, CONFIG, SKIS, QUESTIONS };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init()); else init();
})();
